import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatDayHeading, formatTime } from '../calendar/dates';
import { formatMoney, parseMoney } from '../finance/money';
import { useMe } from '../session/SessionProvider';
import type { MinistriesOverview } from '../../shared/api/types';

type Base = { id: string; name: string; priceCents: number; description: string | null; position: number };
type MenuItem = Base & { kind: 'fixed' | 'day'; soldOut: boolean; hidden: boolean; regularPriceCents: number | null };
export type CafeteriaMenu = { open: boolean; byType: boolean; notes: string | null; items: MenuItem[]; canEdit: boolean; canEditFixed: boolean };
type Setup = {
  canSetup: boolean;
  canEdit: boolean;
  types: { id: string; name: string; weekday: number | null; startTime: string | null; open: boolean; fixed: Base[] }[];
  services: { id: string; title: string; startsAt: string; items: number }[];
};
type Msg = { tone: 'error' | 'success'; text: string } | null;

const money = (cents: number) => formatMoney(cents, 'MXN');
const WEEKDAYS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

/** Ejecuta una llamada con su aviso de error o de éxito. */
function useRunner(onChange: () => void) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);
  async function run(action: () => Promise<unknown>, ok?: string): Promise<boolean> {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      if (ok) setMsg({ tone: 'success', text: ok });
      onChange();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  }
  return { busy, msg, setMsg, run };
}

function readItem(form: HTMLFormElement, setMsg: (m: Msg) => void) {
  const f = new FormData(form);
  const name = String(f.get('name') ?? '').trim();
  const priceCents = parseMoney(String(f.get('price') ?? ''));
  if (!name) return setMsg({ tone: 'error', text: 'Escribe qué se vende.' }), null;
  if (!priceCents) return setMsg({ tone: 'error', text: 'Escribe el precio.' }), null;
  return { name, priceCents, description: String(f.get('description') ?? '').trim() || null };
}

/**
 * Pestaña «Cafetería» del culto: qué se vende y a qué precio. Primero salen los artículos fijos de su tipo de culto
 * y luego lo del día. La escribe el ministerio de Cafetería; la ve toda la iglesia.
 */
export function CafeteriaCard({ base, menu: m, onChange }: { base: string; menu: CafeteriaMenu; onChange: () => void }) {
  const url = `${base}/cafeteria`;
  const { busy, msg, setMsg, run } = useRunner(onChange);
  const [editing, setEditing] = useState<string | null>(null);
  const [notes, setNotes] = useState<string | null>(null);

  function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const body = readItem(form, setMsg);
    if (body) void run(() => api.post(`${url}/items`, body)).then((ok) => ok && form.reset());
  }

  function save(e: FormEvent<HTMLFormElement>, i: MenuItem) {
    e.preventDefault();
    if (i.kind === 'fixed') {
      const priceCents = parseMoney(String(new FormData(e.currentTarget).get('price') ?? ''));
      if (!priceCents) return setMsg({ tone: 'error', text: 'Escribe el precio.' });
      void run(() => api.put(`${url}/fixed/${i.id}`, { priceCents }), `${i.name}: precio de hoy guardado.`).then((ok) => ok && setEditing(null));
      return;
    }
    const body = readItem(e.currentTarget, setMsg);
    if (body) void run(() => api.put(`${url}/items/${i.id}`, body)).then((ok) => ok && setEditing(null));
  }

  const soldOut = (i: MenuItem, value: boolean) =>
    run(() => (i.kind === 'fixed' ? api.put(`${url}/fixed/${i.id}`, { soldOut: value }) : api.put(`${url}/items/${i.id}/sold-out`, { soldOut: value })));

  if (!m.open) {
    return (
      <Card title="Cafetería">
        {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
        <p className="muted">La cafetería no abre en este culto.</p>
        {m.canEdit ? (
          <Button variant="secondary" busy={busy} onClick={() => void run(() => api.put(url, { open: true }), 'Cafetería abierta en este culto.')}>
            Abrir cafetería en este culto
          </Button>
        ) : null}
        {m.canEdit ? <RuleLink /> : null}
      </Card>
    );
  }

  const visible = m.items.filter((i) => !i.hidden);
  const hidden = m.items.filter((i) => i.hidden);

  return (
    <Card title="Cafetería">
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {m.notes && notes === null ? <p className="pre-line">{m.notes}</p> : null}
      {visible.length === 0 ? <p className="muted">{m.canEdit ? 'Escribe qué se venderá en este culto y a qué precio.' : 'Cafetería todavía no publica qué venderá.'}</p> : null}
      <ul className="list" aria-label="Menú de la cafetería">
        {visible.map((i) =>
          editing === i.id ? (
            <li key={i.id} className="list__item">
              <form className="form" onSubmit={(e) => save(e, i)} aria-label={`Editar ${i.name}`}>
                {i.kind === 'fixed' ? (
                  <div className="field">
                    <label htmlFor={`cafe-${i.id}-price`}>Precio de {i.name} solo en este culto</label>
                    <input id={`cafe-${i.id}-price`} name="price" required inputMode="decimal" defaultValue={(i.priceCents / 100).toFixed(2)} />
                    <span className="small muted">El de siempre es {money(i.regularPriceCents ?? i.priceCents)}. La lista fija no cambia.</span>
                  </div>
                ) : (
                  <ItemFields item={i} />
                )}
                <div className="actions">
                  <Button type="submit" busy={busy}>
                    Guardar
                  </Button>
                  {i.kind === 'fixed' && i.regularPriceCents !== null ? (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => void run(() => api.put(`${url}/fixed/${i.id}`, { priceCents: null }), `${i.name} regresa a su precio de siempre.`).then((ok) => ok && setEditing(null))}
                    >
                      Precio de siempre
                    </Button>
                  ) : null}
                  <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </li>
          ) : (
            <li key={i.id} className="list__item list__item--actions">
              <span className="agenda__body">
                <strong className={i.soldOut ? 'struck' : undefined}>{i.name}</strong>
                {i.description ? <span className="small muted">{i.description}</span> : null}
                {m.canEdit && i.kind === 'fixed' ? <span className="small muted">Fijo de este tipo de culto</span> : null}
              </span>
              {i.soldOut ? <span className="badge badge--warn">Se acabó</span> : null}
              <span>
                {i.regularPriceCents !== null ? <span className="struck small">{money(i.regularPriceCents)}</span> : null} <strong>{money(i.priceCents)}</strong>
              </span>
              {m.canEdit ? (
                <span className="actions">
                  <Button variant="ghost" disabled={busy} aria-label={i.soldOut ? `Hay otra vez ${i.name}` : `Se acabó ${i.name}`} onClick={() => void soldOut(i, !i.soldOut)}>
                    {i.soldOut ? 'Hay otra vez' : 'Se acabó'}
                  </Button>
                  <Button variant="ghost" disabled={busy} aria-label={i.kind === 'fixed' ? `Precio de hoy de ${i.name}` : `Editar ${i.name}`} onClick={() => setEditing(i.id)}>
                    {i.kind === 'fixed' ? 'Precio de hoy' : 'Editar'}
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={busy}
                    aria-label={i.kind === 'fixed' ? `Quitar hoy ${i.name}` : `Quitar ${i.name}`}
                    onClick={() => void run(() => (i.kind === 'fixed' ? api.put(`${url}/fixed/${i.id}`, { hidden: true }) : api.delete(`${url}/items/${i.id}`)))}
                  >
                    {i.kind === 'fixed' ? 'Quitar hoy' : 'Quitar'}
                  </Button>
                </span>
              ) : null}
            </li>
          ),
        )}
      </ul>

      {m.canEdit && hidden.length ? (
        <div>
          <p className="small muted">Fijos que no se venden en este culto:</p>
          <ul className="list" aria-label="Fijos quitados en este culto">
            {hidden.map((i) => (
              <li key={i.id} className="list__item list__item--actions">
                <span className="agenda__body muted">{i.name}</span>
                <Button variant="ghost" disabled={busy} aria-label={`Regresar ${i.name}`} onClick={() => void run(() => api.put(`${url}/fixed/${i.id}`, { hidden: false }))}>
                  Regresar
                </Button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {m.canEdit ? (
        <>
          <form className="form" onSubmit={add} aria-label="Agregar producto">
            <p className="small muted">Lo de hoy (solo en este culto):</p>
            <ItemFields item={null} />
            <Button type="submit" variant="secondary" busy={busy}>
              Agregar
            </Button>
          </form>
          {notes === null ? (
            <div className="actions">
              <Button variant="ghost" onClick={() => setNotes(m.notes ?? '')}>
                {m.notes ? 'Editar nota' : 'Agregar nota'}
              </Button>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  if (window.confirm('¿Cerrar la cafetería en este culto?')) void run(() => api.put(url, { open: false }), 'La cafetería no abrirá en este culto.');
                }}
              >
                No abre en este culto
              </Button>
            </div>
          ) : (
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                void run(() => api.put(url, { notes: notes.trim() || null })).then((ok) => ok && setNotes(null));
              }}
            >
              <div className="field">
                <label htmlFor="cafe-notes">Nota para la iglesia</label>
                <textarea id="cafe-notes" rows={2} maxLength={1000} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Lo recaudado es para el campamento de jóvenes." />
              </div>
              <div className="actions">
                <Button type="submit" busy={busy}>
                  Guardar nota
                </Button>
                <Button type="button" variant="ghost" onClick={() => setNotes(null)}>
                  Cancelar
                </Button>
              </div>
            </form>
          )}
        </>
      ) : null}
      {m.canEdit ? <RuleLink /> : null}
    </Card>
  );
}

/** Para el ministerio de Cafetería: la regla general (en qué tipos de culto abre y sus fijos) vive en su ficha, pestaña «Cultos». */
function RuleLink() {
  const churchId = useMe().activeChurchId;
  const ministries = useAsync(
    (signal) => (churchId ? api.get<MinistriesOverview>(`/organization/churches/${churchId}/ministries`, signal).catch(() => null) : Promise.resolve(null)),
    [churchId],
  );
  const id = ministries.status === 'ready' ? ministries.data?.items.find((x) => x.systemKey === 'cafeteria')?.id : undefined;
  if (!id) return null;
  return (
    <p className="small">
      <Link to={`/app/ministerios/${id}?ver=cultos`}>¿En qué cultos hay cafetería y qué es fijo?</Link>
    </p>
  );
}

function ItemFields({ item, prefix }: { item: Base | null; prefix?: string }) {
  const p = prefix ?? (item ? `cafe-${item.id}` : 'cafe-new');
  return (
    <div className="form__row">
      <div className="field">
        <label htmlFor={`${p}-name`}>Qué se vende</label>
        <input id={`${p}-name`} name="name" required maxLength={120} defaultValue={item?.name ?? ''} placeholder="Tamales" />
      </div>
      <div className="field">
        <label htmlFor={`${p}-price`}>Precio</label>
        <input id={`${p}-price`} name="price" required inputMode="decimal" defaultValue={item ? (item.priceCents / 100).toFixed(2) : ''} placeholder="25.00" />
      </div>
      <div className="field">
        <label htmlFor={`${p}-desc`}>Detalle</label>
        <input id={`${p}-desc`} name="description" maxLength={300} defaultValue={item?.description ?? ''} placeholder="Opcional: de rajas o de pollo" />
      </div>
    </div>
  );
}

/** Los artículos fijos de un tipo de culto: salen solos en cada culto de ese tipo. */
function FixedCard({ url, type, canEdit, onChange }: { url: string; type: Setup['types'][number]; canEdit: boolean; onChange: () => void }) {
  const { busy, msg, setMsg, run } = useRunner(onChange);
  const [editing, setEditing] = useState<string | null>(null);
  const p = `fixed-${type.id}`;

  function add(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const body = readItem(form, setMsg);
    if (body) void run(() => api.post(`${url}/fixed`, { ...body, serviceTypeId: type.id }), `${body.name} se venderá en cada ${type.name}.`).then((ok) => ok && form.reset());
  }

  function save(e: FormEvent<HTMLFormElement>, id: string) {
    e.preventDefault();
    const body = readItem(e.currentTarget, setMsg);
    if (body) void run(() => api.put(`${url}/fixed/${id}`, body)).then((ok) => ok && setEditing(null));
  }

  return (
    <Card title={`Fijos en ${type.name}`}>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      <p className="small muted">Salen solos en cada {type.name}. En un culto se pueden quitar, cambiar de precio ese día o marcar «Se acabó».</p>
      {type.fixed.length === 0 ? <p className="muted">Sin artículos fijos todavía.</p> : null}
      <ul className="list" aria-label={`Fijos en ${type.name}`}>
        {type.fixed.map((f) =>
          editing === f.id ? (
            <li key={f.id} className="list__item">
              <form className="form" onSubmit={(e) => save(e, f.id)}>
                <ItemFields item={f} prefix={`${p}-${f.id}`} />
                <div className="actions">
                  <Button type="submit" busy={busy}>
                    Guardar
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </li>
          ) : (
            <li key={f.id} className="list__item list__item--actions">
              <span className="agenda__body">
                <strong>{f.name}</strong>
                {f.description ? <span className="small muted">{f.description}</span> : null}
              </span>
              <strong>{money(f.priceCents)}</strong>
              {canEdit ? (
                <span className="actions">
                  <Button variant="ghost" disabled={busy} aria-label={`Editar ${f.name}`} onClick={() => setEditing(f.id)}>
                    Editar
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={busy}
                    aria-label={`Quitar ${f.name}`}
                    onClick={() => {
                      if (window.confirm(`¿Quitar ${f.name} de los fijos de ${type.name}? Ya no saldrá en ningún culto de este tipo.`)) void run(() => api.delete(`${url}/fixed/${f.id}`));
                    }}
                  >
                    Quitar
                  </Button>
                </span>
              ) : null}
            </li>
          ),
        )}
      </ul>
      {canEdit ? (
        <form className="form" onSubmit={add} aria-label={`Agregar fijo en ${type.name}`}>
          <ItemFields item={null} prefix={`${p}-new`} />
          <Button type="submit" variant="secondary" busy={busy}>
            Agregar fijo
          </Button>
        </form>
      ) : null}
    </Card>
  );
}

/** Ministerio de Cafetería: en qué tipos de culto abre y los próximos cultos con su menú. */
export function CafeteriaSetupCard({ churchId }: { churchId: string }) {
  const url = `/worship/churches/${churchId}/cafeteria`;
  const data = useAsync((signal) => api.get<Setup>(url, signal), [url]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  if (data.status === 'loading') return <Loading />;
  if (data.status === 'error') return <Alert>{errorMessage(data.error)}</Alert>;
  const s = data.data;

  async function toggle(typeId: string, on: boolean) {
    const ids = s.types.filter((t) => (t.id === typeId ? on : t.open)).map((t) => t.id);
    setBusy(true);
    setMsg(null);
    try {
      await api.put(`${url}/service-types`, { serviceTypeIds: ids });
      data.reload();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Card title="Cultos con cafetería">
        {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
        {s.types.length === 0 ? (
          <p className="muted">La iglesia todavía no tiene tipos de culto. Se crean en <Link to="/app/cultos">Cultos</Link>.</p>
        ) : (
          <>
            <p className="small muted">{s.canSetup ? 'Marca los cultos en los que abre la cafetería. Los demás se abren a mano en el culto que haga falta.' : 'La cafetería abre en estos cultos.'}</p>
            <ul className="checklist">
              {s.types.map((t) => (
                <li key={t.id} className="checklist__item">
                  <label className="check">
                    <input type="checkbox" checked={t.open} disabled={!s.canSetup || busy} onChange={(e) => void toggle(t.id, e.target.checked)} /> {t.name}
                    {t.weekday !== null ? <span className="small muted"> · {WEEKDAYS[t.weekday]}{t.startTime ? ` ${t.startTime.slice(0, 5)}` : ''}</span> : null}
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>
      {s.types
        .filter((t) => t.open || t.fixed.length > 0)
        .map((t) => (
          <FixedCard key={t.id} url={url} type={t} canEdit={s.canSetup} onChange={data.reload} />
        ))}
      <Card title="Próximos cultos">
        {s.services.length === 0 ? <p className="muted">No hay cultos próximos con cafetería.</p> : null}
        <ul className="list">
          {s.services.map((x) => (
            <li key={x.id} className="list__item list__item--actions">
              <Link to={`/app/cultos/${x.id}?ver=cafeteria`} className="agenda__body">
                <strong>{x.title}</strong>
                <span className="small muted">
                  {formatDayHeading(new Date(x.startsAt))} · {formatTime(x.startsAt)}
                </span>
              </Link>
              <span className={`badge${x.items ? ' badge--active' : ''}`}>{x.items ? `${x.items} ${x.items === 1 ? 'producto' : 'productos'}` : 'Sin menú'}</span>
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}
